import React from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiMapPin, FiTwitter, FiLinkedin, FiGithub, FiArrowUp, FiLock } from 'react-icons/fi';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Services',
      links: [
        { name: 'Software Development', href: '/services' },
        { name: 'AI & Machine Learning', href: '/services' },
        { name: 'Cloud Solutions', href: '/services' },
        { name: 'Cybersecurity', href: '/services' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Careers', href: '/careers' },
        { name: 'Contact', href: '/contact' },
        { name: 'Blog', href: '/blog' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Documentation', href: '#' },
        { name: 'Support Center', href: '#' },
        { name: 'Victory Fund Privacy Policy', href: '/privacy-policy/victory-fund' },
        { name: 'Delete Victory Fund Account', href: '/victory-fund/account-deletion' },
        { name: 'Terms of Service', href: '#' }
      ]
    }
  ];

  const socialLinks = [
    { icon: FiTwitter, href: '#', label: 'Twitter' },
    { icon: FiLinkedin, href: '#', label: 'LinkedIn' },
    { icon: FiGithub, href: '#', label: 'GitHub' }
  ];

  return (
    <footer className="relative bg-gray-100 dark:bg-gradient-to-t dark:from-dark-950 dark:to-dark-900 border-t border-gray-200 dark:border-white/10">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2338bdf8' fill-opacity='0.08'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-10 sm:mb-12">

          {/* Logo-only block (left) - large and centered like the navbar */}
          <div className="lg:col-span-1 flex flex-col items-center lg:items-start">
            <div className="w-full flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
              <div className="h-16 w-16 sm:h-20 sm:w-20 bg-gradient-primary rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-gray-900 dark:text-white font-bold text-xl sm:text-2xl">N</span>
              </div>
            </div>

            {/* Visually hidden company name/tagline for accessibility */}
            <div className="sr-only">
              <h3>NeoVam</h3>
              <p>AI · Cloud · Fintech</p>
            </div>

            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed text-center lg:text-left">
              We are an AI-powered technology company delivering cloud, fintech, and software innovation across Africa.
            </p>

            <div className="space-y-2 sm:space-y-3 w-full">
              <div className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <FiMail className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <a href="mailto:info@neovam.com" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors break-all">
                  info@neovam.com
                </a>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <FiMapPin className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <a href="https://maps.app.goo.gl/vehggr7yykbAxp8SA" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-center lg:text-left">P.O BOX 36098 Kigamboni Dar es Salaam</a>
              </div>
            </div>
          </div>

          {/* Footer Columns */}
          {footerSections.map((section, index) => (
            <div key={index} className="text-center lg:text-left">
              <h4 className="text-sm sm:text-base text-gray-900 dark:text-white font-semibold mb-3 sm:mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.href}
                      className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-xs sm:text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 mb-10 sm:mb-12 border border-primary-300 dark:border-primary-400/20">
          <div className="text-center max-w-md mx-auto">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2">Stay Updated</h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6">
              Get the latest updates on AI, cloud technologies, and industry insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary-600 dark:focus:border-primary-400 transition-colors"
              />
              <button className="px-5 sm:px-6 py-3 bg-gradient-primary text-white text-sm font-semibold rounded-lg hover:shadow-glow transition-all duration-300 touch-manipulation whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-6 sm:pt-8 border-t border-gray-300 dark:border-white/10 gap-4">
          {/* Copyright */}
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm text-center md:text-left">
            &copy; {currentYear} NeoVam Technologies. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-3 sm:gap-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                aria-label={social.label}
                className="p-2.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-400/10 rounded-lg transition-all duration-300 touch-manipulation"
              >
                <social.icon size={18} />
              </a>
            ))}
          </div>

          {/* Admin Login (small, positioned like Back to top) */}
          <div className="flex items-center gap-4">
            <Link
              to="/admin/login"
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors group"
            >
              <FiLock size={12} className="group-hover:scale-105 transition-transform" />
              <span>Admin</span>
            </Link>

            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 px-4 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors group touch-manipulation"
            >
              <span>Back to top</span>
              <FiArrowUp className="group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;